import { signal } from '@angular/core';
import { Subject } from 'rxjs';
import { IFilterCardValuePatch, TFilterCardFieldConfig, TFilterCardValue } from './filter-card.interface';
import { FilterCardService } from './filter-card.service';

/**
 * The single channel between a page and its `FilterCardComponent`. The page never needs a reference
 * to the component itself, so the filter card can be rendered anywhere in the tree.
 */
export class FilterCardManager<Name extends string> {
  /** The pool of addable field configs — not everything in here is currently rendered as a chip. */
  public readonly fields = signal<TFilterCardFieldConfig<Name>[]>([]);
  /** Which pool fields currently have a chip. Fields outside `fields()` (e.g. a page's own toggles
   * that share this manager without going through the add/remove UI) are never tracked here. */
  public readonly activeFieldNames = signal<Name[]>([]);
  /** Last value the user applied (or the filter card applied on the page's behalf). */
  public readonly appliedValue = signal<TFilterCardValue<Name> | null>(null);
  public readonly setAsDefault$ = new Subject<TFilterCardValue<Name>>();
  /**
   * Enforces "one open chip/add-form at a time": a chip entering edit mode, or the add-form entering
   * picking mode, publishes its own identity here; every *other* listener closes itself on any
   * broadcast whose identity isn't its own (a fresh `Symbol()` — which matches nothing — closes every
   * listener at once, which is what `FilterCardComponent.apply()` publishes). Mirrors digital-boards'
   * `AdvancedFilterComponent.oneEditOperationAtATimeSubject`.
   */
  public readonly oneOpenFormAtATime$ = new Subject<symbol>();
  /**
   * Post-init pushes only (KPI card clicks, kamishibai nav, ...) — `FilterCardComponent` only starts
   * listening in its own `ngOnInit`, which Angular always runs *after* its host's `ngOnInit`, so a
   * patch pushed synchronously from the host's `ngOnInit` (e.g. a route-resolved default) would be
   * dropped with nobody subscribed yet. Route-derived seed values belong in `initialValue` /
   * `patchInitialValue` instead, which are read once when the form is first built.
   */
  public readonly valuePatch$ = new Subject<IFilterCardValuePatch<Name>>();

  constructor(
    fields: TFilterCardFieldConfig<Name>[] = [],
    /** The seed value, read once when the filter card first builds its form. */
    public readonly initialValue: TFilterCardValue<Name> = {},
    public readonly hasSetAsDefault = true,
  ) {
    this.fields.set(fields);
    this.activeFieldNames.set(
      fields
        .filter((field) => FilterCardService.hasFilterCardValue(initialValue[field.name]))
        .map((field) => field.name),
    );
  }

  public activateField(name: Name): void {
    if (!this.activeFieldNames().includes(name)) {
      this.activeFieldNames.update((names) => [...names, name]);
    }
  }

  public deactivateField(name: Name): void {
    this.activeFieldNames.update((names) => names.filter((existingName) => existingName !== name));
  }

  /** Merges into `initialValue`. Must be called before the filter card reads it for the first time —
   * safe to call from the host's `ngOnInit`, unlike `patchValue`. */
  public patchInitialValue(patch: TFilterCardValue<Name>): void {
    Object.assign(this.initialValue, patch);
    this.syncActivation(patch);
  }

  public patchValue(patch: TFilterCardValue<Name>, apply = false): void {
    this.syncActivation(patch);
    this.valuePatch$.next({ apply, patch });
  }

  /** A value arriving for a pool field — whether seeded before init or pushed live — always means
   * "this filter is on"; an explicit empty value (e.g. a KPI card clearing the status filter) means
   * the opposite. Fields outside the pool (a page's own toggles sharing this manager) are untouched. */
  private syncActivation(patch: TFilterCardValue<Name>): void {
    const poolFieldNames = new Set(this.fields().map((field) => field.name));

    for (const [name, value] of Object.entries(patch) as [Name, unknown][]) {
      if (!poolFieldNames.has(name)) {
        continue;
      }

      if (FilterCardService.hasFilterCardValue(value)) {
        this.activateField(name);
      } else {
        this.deactivateField(name);
      }
    }
  }
}
